import React, { useEffect, useState } from 'react';
import { Check, MapPin, RefreshCw } from 'lucide-react';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { StcBinDto } from '../../../../../types/stcBin';

export interface FinishReceivingLocationModalProps {
    isOpen: boolean;
    /** Mottakslokasjoner (StcBin med kategori Varemottak) i lageret — vises kun når det er flere enn én. */
    locations: StcBinDto[];
    /** Forhåndsvalgt lokasjon (Profile.DefaultGRBinCode når den finnes i lista, ellers linjenes ankomstlokasjon). */
    suggestedBinCode: string | null;
    /** Antall ankomstregistrerte linjer som plasseres. */
    lineCount: number;
    isSubmitting: boolean;
    onConfirm: (binCode: string) => void;
    onCancel: () => void;
}

/**
 * «Avslutt mottak» i GO-lagre (TW 36382686): alle ankomstregistrerte linjer plasseres på
 * ÉN mottakslokasjon og mottaket eksporteres til ERP. Dialogen vises bare når lageret har
 * flere mottakslokasjoner — forslaget er forhåndsvalgt, operatøren kan overstyre, og én
 * stor OK-knapp tar alle linjene. Nettbrett: store trykkmål, ingen hover-avhengig informasjon.
 */
export const FinishReceivingLocationModal: React.FC<FinishReceivingLocationModalProps> = ({
    isOpen,
    locations,
    suggestedBinCode,
    lineCount,
    isSubmitting,
    onConfirm,
    onCancel
}) => {
    const { t } = useTranslation();
    const [selected, setSelected] = useState<string>('');

    // Forslaget settes hver gang dialogen åpnes (ikke bare første gang komponenten monteres).
    useEffect(() => {
        if (!isOpen) return;
        if (suggestedBinCode && locations.some(l => l.binCode === suggestedBinCode)) {
            setSelected(suggestedBinCode);
        } else {
            setSelected(locations[0]?.binCode ?? '');
        }
    }, [isOpen, suggestedBinCode, locations]);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                <div className="bg-green-500 text-white px-6 py-4 rounded-t-lg">
                    <div className="flex items-center">
                        <MapPin className="h-6 w-6 mr-3 flex-shrink-0" />
                        <h3 className="text-lg font-semibold">
                            {t('warehouse.modules.receipt.finishLocation.title', {}, 'Velg mottakslokasjon')}
                        </h3>
                    </div>
                </div>

                <div className="px-6 py-4 overflow-y-auto">
                    <p className="text-gray-700 text-sm mb-3">
                        {t('warehouse.modules.receipt.finishLocation.description', { '0': String(lineCount) },
                            `${lineCount} ankomstregistrerte linje(r) plasseres på valgt lokasjon, og mottaket eksporteres til ERP.`)}
                    </p>

                    <div className="space-y-2" role="radiogroup">
                        {locations.map((loc) => {
                            const isSelected = loc.binCode === selected;
                            const isSuggested = loc.binCode === suggestedBinCode;
                            return (
                                <button
                                    key={loc.binCode}
                                    type="button"
                                    role="radio"
                                    aria-checked={isSelected}
                                    disabled={isSubmitting}
                                    onClick={() => setSelected(loc.binCode)}
                                    className={`w-full min-h-[48px] flex items-center justify-between gap-3 px-4 py-3 rounded-lg border-2 text-left transition-colors ${
                                        isSelected
                                            ? 'border-green-500 bg-green-50 text-green-900'
                                            : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                                    }`}
                                >
                                    <div className="min-w-0">
                                        <div className="font-semibold truncate">{loc.binCode}</div>
                                        {loc.description1 && (
                                            <div className="text-xs text-gray-500 truncate">{loc.description1}</div>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                        {isSuggested && (
                                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                                                {t('warehouse.modules.receipt.finishLocation.suggested', {}, 'Forslag')}
                                            </span>
                                        )}
                                        {isSelected && <Check className="h-5 w-5 text-green-600" />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={isSubmitting}
                        className="px-4 py-3 min-h-[44px] bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors font-medium disabled:opacity-50"
                    >
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                    <button
                        type="button"
                        onClick={() => selected && onConfirm(selected)}
                        disabled={isSubmitting || !selected}
                        className="flex-1 sm:flex-none sm:min-w-[200px] px-6 py-3 min-h-[52px] flex items-center justify-center gap-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? (
                            <RefreshCw className="h-5 w-5 animate-spin" />
                        ) : (
                            <Check className="h-5 w-5" />
                        )}
                        {isSubmitting
                            ? t('warehouse.modules.receipt.actions.finishing', {}, 'Avslutter...')
                            : t('warehouse.modules.receipt.finishLocation.confirm', { '0': selected }, `OK — avslutt på ${selected}`)}
                    </button>
                </div>
            </div>
        </div>
    );
};
